/**
 * Admin → Data providers: market-data API keys, which provider prices each asset class (and which
 * take over when it can't), and how much of each provider's free plan is in use. Admin only.
 * Keys are write-only: the page only ever gets their last four characters back. Saving reconnects
 * the provider within seconds; nothing restarts.
 */
import { useCallback, useEffect, useState, type DragEvent, type FormEvent } from 'react';
import {
  fmtDateTime,
  PROVIDER_BY_ID,
  PROVIDERS,
  ROUTE_CLASS_LABEL,
  ROUTE_CLASSES,
  type FeedAdapterStatus,
  type FeedRouting,
  type PriceProviderId,
  type ProviderId,
  type ProviderSummary,
  type ProviderTestResult,
  type ProviderUsage,
  type RouteClass,
} from '@platform/shared';
import { cx, Icon, Modal, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const secondary = 'h-9 rounded-lg border border-line px-4 text-xs font-semibold text-fg disabled:opacity-50';
const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2.5 whitespace-nowrap';
const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

type Row = ProviderSummary & { feed: FeedAdapterStatus | null; usage: ProviderUsage };
interface Data {
  providers: Row[];
  routing: FeedRouting;
  secretsKeySet: boolean;
  feeder: boolean;
}

const LOGO: Record<ProviderId, string> = {
  binance: '#c99400',
  kraken: '#5741d9',
  oanda: '#e4002b',
  alpaca: '#d9a400',
  finnhub: '#1db954',
  twelvedata: '#2f6bff',
  coingecko: '#7cb342',
  eia: '#0b5a8c',
  bls: '#1f4e79',
  fred: '#00508f',
};
const initials = (id: ProviderId) => PROVIDER_BY_ID[id].label.replace(/[^A-Z]/g, '').slice(0, 2) || PROVIDER_BY_ID[id].label.slice(0, 2).toUpperCase();

function ProviderLogo({ id }: { id: ProviderId }) {
  return (
    <span className="inline-grid size-6 shrink-0 place-items-center rounded-md text-[9.5px] font-extrabold text-white" style={{ background: LOGO[id] }} aria-hidden>
      {initials(id)}
    </span>
  );
}

function Status({ p }: { p: Row }) {
  const def = PROVIDER_BY_ID[p.id];
  if (p.problem) return <span className="rounded-md bg-down-bg px-2 py-0.5 text-[11px] font-semibold text-down">● Key unreadable</span>;
  if (!p.configured) return <span className="rounded-md bg-raised px-2 py-0.5 text-[11px] font-semibold text-muted">○ No key</span>;
  if (!def.classes.length) return <span className="rounded-md bg-up-bg px-2 py-0.5 text-[11px] font-semibold text-up">● Ready</span>;
  const f = p.feed;
  if (!f || f.state === 'off') return <span className="rounded-md bg-raised px-2 py-0.5 text-[11px] font-semibold text-muted">○ Not in use</span>;
  if (f.state === 'up') return <span className="rounded-md bg-up-bg px-2 py-0.5 text-[11px] font-semibold text-up">● Up</span>;
  return (
    <span className="rounded-md bg-down-bg px-2 py-0.5 text-[11px] font-semibold text-down" title={f.lastError ?? f.detail ?? undefined}>
      ● {/40[13]|auth|unauthori|forbidden|invalid/i.test(f.lastError ?? '') ? 'Key rejected' : 'Down'}
    </span>
  );
}

function Bar({ used, of }: { used: number; of: number | null }) {
  const pct = of ? Math.min(100, Math.round((used / of) * 100)) : used ? 30 : 0;
  return (
    <span className="mr-2 inline-block h-1.5 w-20 overflow-hidden rounded bg-raised align-middle">
      <i className={cx('block h-full', pct >= 90 ? 'bg-warn' : 'bg-up')} style={{ width: `${pct}%` }} />
    </span>
  );
}

function Usage({ p }: { p: Row }) {
  const f = p.feed;
  const parts: string[] = [];
  if (f && f.state !== 'off') {
    parts.push(f.slots ? `${f.streaming}/${f.slots} stream` : `${f.streaming} symbols`);
    if (p.restPerMin) parts.push(`${p.usage.lastMinute}/${p.restPerMin} rpm`);
  } else if (p.plan.perMonth) parts.push(`${p.usage.month.toLocaleString()}/${p.plan.perMonth.toLocaleString()} this month`);
  else if (p.plan.perDay) parts.push(`${p.usage.today.toLocaleString()}/${p.plan.perDay.toLocaleString()} today`);
  else if (!p.configured) parts.push('—');
  else parts.push('standby');
  // Idle providers show their plan: this month's or today's calls against it (the free plan's unless set).
  const idlePlan = !f || f.state === 'off';
  const used = f?.slots ? f.streaming : p.restPerMin ? p.usage.lastMinute : idlePlan && p.plan.perMonth ? p.usage.month : idlePlan && p.plan.perDay ? p.usage.today : 0;
  const of = f?.slots ?? p.restPerMin ?? (idlePlan ? (p.plan.perMonth ?? p.plan.perDay) : null);
  return (
    <span className="text-muted">
      <Bar used={used} of={of} />
      {parts.join(' · ')}
    </span>
  );
}

function KeyEditor({ p, secretsKeySet, onClose, onSaved }: { p: Row; secretsKeySet: boolean; onClose: () => void; onSaved: () => void }) {
  const def = PROVIDER_BY_ID[p.id];
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(def.fields.map((f) => [f.name, f.secret ? '' : (p.values[f.name] ?? f.options?.[0]?.value ?? '')])));
  const [rest, setRest] = useState(p.restPerMin?.toString() ?? '');
  const [slots, setSlots] = useState(p.streamSlots?.toString() ?? '');
  const [plan, setPlan] = useState(() => ({
    perMin: p.plan.perMin?.toString() ?? '',
    perDay: p.plan.perDay?.toString() ?? '',
    perMonth: p.plan.perMonth?.toString() ?? '',
  }));
  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  const [test, setTest] = useState<ProviderTestResult | null>(null);
  const [busy, setBusy] = useState<'test' | 'save' | 'remove' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const body = () => ({
    values: vals,
    ...(def.defaultRestPerMin !== null ? { restPerMin: rest.trim() === '' ? null : Number(rest) } : {}),
    ...(def.defaultStreamSlots !== null ? { streamSlots: slots.trim() === '' ? null : Number(slots) } : {}),
    planLimits: { perMin: num(plan.perMin), perDay: num(plan.perDay), perMonth: num(plan.perMonth) },
  });
  const run = async (what: 'test' | 'save' | 'remove', fn: () => Promise<void>) => {
    setBusy(what);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    void run('save', async () => {
      await api.put(`/api/admin/providers/${p.id}`, body());
      onSaved();
      onClose();
    });
  };
  return (
    <Modal open onClose={onClose} title={def.label} width={480}>
      <form onSubmit={save} className="flex flex-col gap-3">
        {!secretsKeySet && def.fields.some((f) => f.secret) && (
          <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">
            Saving keys here needs SECRETS_ENCRYPTION_KEY in the server .env. Until then the keys in .env are used.
          </p>
        )}
        {p.problem && <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-xs text-down">{p.problem}</p>}
        {def.fields.map((f) => (
          <label key={f.name} className="flex flex-col gap-1.5 text-[11.5px] font-semibold text-muted">
            {f.label}
            {f.options ? (
              <span className="inline-flex w-fit overflow-hidden rounded-lg border border-line">
                {f.options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={vals[f.name] === o.value}
                    onClick={() => setVals((v) => ({ ...v, [f.name]: o.value }))}
                    className={cx('h-8 px-3.5 text-xs font-semibold', vals[f.name] === o.value ? 'bg-accent text-white' : 'text-muted')}
                  >
                    {o.label}
                  </button>
                ))}
              </span>
            ) : (
              <input
                className={cx(input, 'num font-normal')}
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                spellCheck={false}
                value={vals[f.name]}
                placeholder={f.secret && p.values[f.name] ? `${p.values[f.name]} (leave blank to keep)` : f.placeholder}
                onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))}
              />
            )}
          </label>
        ))}
        <div className="flex gap-3">
          {def.defaultRestPerMin !== null && (
            <label className="flex flex-1 flex-col gap-1.5 text-[11.5px] font-semibold text-muted">
              REST budget (calls/min)
              <input className={cx(input, 'num')} inputMode="numeric" value={rest} placeholder={String(def.defaultRestPerMin)} onChange={(e) => setRest(e.target.value)} />
            </label>
          )}
          {def.defaultStreamSlots !== null && (
            <label className="flex flex-1 flex-col gap-1.5 text-[11.5px] font-semibold text-muted">
              Stream slots
              <input className={cx(input, 'num')} inputMode="numeric" value={slots} placeholder={String(def.defaultStreamSlots)} onChange={(e) => setSlots(e.target.value)} />
            </label>
          )}
        </div>
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 p-0 text-[11.5px] font-semibold text-muted">
            Plan limits{p.planIsDefault ? ' (the free plan’s)' : ''}
          </legend>
          <div className="flex gap-3">
            {(
              [
                ['perMin', 'Calls a minute'],
                ['perDay', 'Calls a day'],
                ['perMonth', 'Calls a month'],
              ] as const
            ).map(([k, label]) => (
              <label key={k} className="flex flex-1 flex-col gap-1.5 text-[11px] font-medium text-muted">
                {label}
                <input className={cx(input, 'num')} inputMode="numeric" value={plan[k]} placeholder="No limit" onChange={(e) => setPlan((v) => ({ ...v, [k]: e.target.value }))} />
              </label>
            ))}
          </div>
          <span className="text-[11px] text-muted">Only watched (usage bars, the CRM’s API monitor): nothing is capped. Empty = no limit of that kind.</span>
        </fieldset>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={secondary} disabled={!!busy} onClick={() => void run('test', async () => setTest(await api.post<ProviderTestResult>(`/api/admin/providers/${p.id}/test`, body())))}>
            {busy === 'test' ? <Spinner /> : 'Test connection'}
          </button>
          {test && (
            <span className={cx('text-xs font-semibold', test.ok ? 'text-up' : 'text-down')}>
              {test.ok ? '✓ Connected · ' : '✗ '}
              {test.detail} · {test.ms} ms
            </span>
          )}
        </div>
        <p className="m-0 rounded-lg bg-accent-bg px-3 py-2 text-xs text-accent-fg">Saving reconnects {def.label} within a few seconds. No restart needed. The change is recorded in the audit log.</p>
        {err && (
          <p className="m-0 text-xs text-down" role="alert">
            {err}
          </p>
        )}
        <div className="flex items-center gap-2">
          {p.source === 'admin' && (
            <button
              type="button"
              className={cx(secondary, 'border-down/40 text-down')}
              disabled={!!busy}
              onClick={() =>
                window.confirm(`Remove the ${def.label} key saved here? The .env key (if any) is used again.`) &&
                void run('remove', async () => {
                  await api.del(`/api/admin/providers/${p.id}`);
                  onSaved();
                  onClose();
                })
              }
            >
              Remove key
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className={secondary} onClick={onClose}>
            Cancel
          </button>
          <button className={primary} disabled={!!busy}>
            {busy === 'save' ? <Spinner /> : 'Save'}
          </button>
        </div>
        {def.signupUrl && (
          <p className="m-0 text-[11px] text-muted">
            No key yet?{' '}
            <a href={def.signupUrl} target="_blank" rel="noreferrer" className="text-accent-fg underline">
              Get a free {def.label} key
            </a>
            .
          </p>
        )}
      </form>
    </Modal>
  );
}

/** One class's provider chain: drag (or use the arrows) to reorder, × to drop, + to add. */
function Chain({ cls, chain, configured, onChange }: { cls: RouteClass; chain: PriceProviderId[]; configured: Record<string, boolean>; onChange: (c: PriceProviderId[]) => void }) {
  const [drag, setDrag] = useState<number | null>(null);
  const candidates = PROVIDERS.filter((p) => p.classes.includes(cls) && !chain.includes(p.id as PriceProviderId)).map((p) => p.id as PriceProviderId);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= chain.length || from === to) return;
    const next = [...chain];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x!);
    onChange(next);
  };
  const onDrop = (e: DragEvent, i: number) => {
    e.preventDefault();
    if (drag !== null) move(drag, i);
    setDrag(null);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chain.map((p, i) => (
        <span key={p} className="flex items-center gap-2">
          {i > 0 && <span className="text-muted">→</span>}
          <span
            draggable
            onDragStart={() => setDrag(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, i)}
            className={cx('flex cursor-grab items-center gap-1.5 rounded-lg border border-line-strong bg-raised py-1 pl-2.5 pr-1 text-xs font-semibold', !configured[p] && 'opacity-60')}
            title={configured[p] ? 'Drag to reorder' : 'No key: skipped until one is added'}
          >
            <span className="font-bold text-muted">{i + 1}</span>
            {PROVIDER_BY_ID[p].label}
            {!configured[p] && <span className="text-[10px] font-normal text-muted">(no key)</span>}
            <button type="button" aria-label={`Move ${PROVIDER_BY_ID[p].label} earlier`} className="px-0.5 text-muted disabled:opacity-30" disabled={i === 0} onClick={() => move(i, i - 1)}>
              ‹
            </button>
            <button type="button" aria-label={`Move ${PROVIDER_BY_ID[p].label} later`} className="px-0.5 text-muted disabled:opacity-30" disabled={i === chain.length - 1} onClick={() => move(i, i + 1)}>
              ›
            </button>
            <button type="button" aria-label={`Remove ${PROVIDER_BY_ID[p].label}`} className="px-0.5 text-muted disabled:opacity-30" disabled={chain.length === 1} onClick={() => onChange(chain.filter((x) => x !== p))}>
              ×
            </button>
          </span>
        </span>
      ))}
      {candidates.length > 0 && (
        <select className="h-8 rounded-lg border border-dashed border-line bg-transparent px-2 text-xs text-muted" value="" onChange={(e) => e.target.value && onChange([...chain, e.target.value as PriceProviderId])} aria-label={`Add a provider to ${ROUTE_CLASS_LABEL[cls]}`}>
          <option value="">+ add</option>
          {candidates.map((c) => (
            <option key={c} value={c}>
              {PROVIDER_BY_ID[c].label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export function ProvidersTab() {
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<Row | null>(null);
  const [routing, setRouting] = useState<FeedRouting | null>(null);
  const [savingRouting, setSavingRouting] = useState(false);
  const [routingMsg, setRoutingMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<Data>('/api/admin/providers');
      setData(d);
      setRouting((r) => r ?? d.routing);
      setErr(null);
    } catch (e) {
      setErr(errText(e));
    }
  }, []);
  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(t);
  }, [load]);

  if (!data || !routing) return err ? <p className="m-0 text-xs text-down">{err}</p> : <Spinner />;
  const configured = Object.fromEntries(data.providers.map((p) => [p.id, p.configured]));
  const dirty = JSON.stringify(routing) !== JSON.stringify(data.routing);

  const saveRouting = async () => {
    setSavingRouting(true);
    setRoutingMsg(null);
    try {
      const r = await api.put<{ routing: FeedRouting }>('/api/admin/feed-routing', routing);
      setRouting(r.routing);
      setRoutingMsg('Saved. The feeds re-route within a few seconds.');
      await load();
    } catch (e) {
      setRoutingMsg(errText(e));
    } finally {
      setSavingRouting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="m-0 text-xs text-muted">API keys, routing and usage for every price and market-info source. Keys are encrypted and never shown again after saving.</p>
      {!data.secretsKeySet && (
        <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">
          SECRETS_ENCRYPTION_KEY is not set on the server, so keys can't be saved here yet; the keys in the server's .env are in use. Generate one with <code>openssl rand -base64 32</code>, add it to the
          .env and keep a copy offline.
        </p>
      )}
      {!data.feeder && <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">The feeder is not running, so connection status is unknown.</p>}

      <section className="flex flex-col gap-2">
        <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted">Providers</h3>
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full border-collapse text-[12.5px]" style={{ minWidth: 820 }}>
            <thead className="border-b border-line">
              <tr>
                {['Provider', 'Used for', 'Status', 'API key', 'Usage', ''].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => {
                const def = PROVIDER_BY_ID[p.id];
                const key = def.fields.find((f) => f.secret);
                return (
                  <tr key={p.id} className="border-b border-line last:border-0">
                    <td className={cx(td, 'font-semibold')}>
                      <span className="flex items-center gap-2.5">
                        <ProviderLogo id={p.id} />
                        {def.label}
                      </span>
                    </td>
                    <td className={cx(td, 'text-muted')}>{def.usedFor}</td>
                    <td className={td}>
                      <Status p={p} />
                    </td>
                    <td className={cx(td, 'num')} title={p.updatedAt ? `Saved ${fmtDateTime(p.updatedAt)}${p.updatedBy ? ` by ${p.updatedBy}` : ''}` : undefined}>
                      {def.keyless && !key ? <span className="font-sans text-muted">Not needed</span> : key && p.values[key.name] ? p.values[key.name] : <span className="text-muted">—</span>}
                      {p.source === 'env' && <span className="ml-1.5 rounded border border-line px-1 font-sans text-[10px] text-muted">.env</span>}
                    </td>
                    <td className={td}>
                      <Usage p={p} />
                    </td>
                    <td className={cx(td, 'text-right')}>
                      {def.fields.length > 0 && (
                        <button type="button" className={cx(p.configured ? secondary : primary, 'h-8 px-3')} onClick={() => setEdit(p)}>
                          {p.configured ? 'Edit' : 'Add key'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted">Routing</h3>
          <span className="text-[11.5px] text-muted">The first provider is used; the next takes over if it fails. Drag to reorder.</span>
        </div>
        <div className="rounded-xl border border-line bg-panel">
          {ROUTE_CLASSES.map((cls) => (
            <div key={cls} className="flex flex-wrap items-center gap-3 border-b border-line px-3 py-3 last:border-0">
              <span className="w-40 shrink-0 text-[12.5px] font-semibold">{ROUTE_CLASS_LABEL[cls]}</span>
              <Chain cls={cls} chain={routing[cls]} configured={configured} onChange={(c) => setRouting({ ...routing, [cls]: c })} />
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 px-3 py-3 text-[12.5px]">
            <span className="w-40 shrink-0 font-semibold">Chart history</span>
            <span className="text-muted">
              The first provider in each class's routing that serves candles (Binance, OANDA, Alpaca); Twelve Data last, as it costs a credit per call.
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          {routingMsg && <span className="text-xs text-muted">{routingMsg}</span>}
          {dirty && (
            <button type="button" className={secondary} onClick={() => setRouting(data.routing)}>
              Undo
            </button>
          )}
          <button type="button" className={primary} disabled={!dirty || savingRouting} onClick={() => void saveRouting()}>
            {savingRouting ? <Spinner /> : 'Save routing'}
          </button>
        </div>
      </section>

      <p className="m-0 flex items-start gap-1.5 text-[11px] text-muted">
        <Icon name="info" size={13} className="mt-px shrink-0" />
        Free plans are usually for personal use. Check each provider's terms before showing its prices to clients; a paid plan is only a key change here.
      </p>
      {edit && <KeyEditor p={edit} secretsKeySet={data.secretsKeySet} onClose={() => setEdit(null)} onSaved={() => void load()} />}
    </div>
  );
}
